import type { AppointmentCardProps } from '@equip.health/ui';
import { compact } from 'lodash';

import { getValueFromQueryParams } from '../utils';

export type AppointmentCardDisplayProps = Pick<
  AppointmentCardProps,
  'assignee' | 'dateTime' | 'id' | 'title'
>;

export interface AppointmentSectionData {
  appointments: AppointmentCardDisplayProps[];
  numUpcoming: number;
}

interface GetAppointmentCardDisplayPropsArgs {
  appointmentExternalId: string;
  appointmentStartDateTime: string;
  host?: AppointmentAttendee | null;
  id?: string;
  title: string;
}

export const getAppointmentCardDisplayName = (
  host?: AppointmentAttendee | null,
): string => {
  if (!host) return '';

  const displayName =
    host.displayFirstName &&
    host.displayLastName &&
    `${host.displayFirstName} ${host.displayLastName}`.trim();

  return displayName || host.name;
};

export const getAppointmentCardDisplayProps = ({
  appointmentExternalId,
  appointmentStartDateTime,
  host,
  id,
  title,
}: GetAppointmentCardDisplayPropsArgs): AppointmentCardDisplayProps => ({
  assignee: {
    id: host?.externalId ?? '',
    name: getAppointmentCardDisplayName(host),
    src: host?.avatar?.thumbnailUrl || host?.avatar?.url,
    userType: 'provider',
  },
  dateTime: new Date(appointmentStartDateTime),
  id: id ?? appointmentExternalId,
  title,
});

export const getAppointmentFromExternalId = (
  appointments: AppointmentDetailResponse[],
  externalId: string,
): AppointmentDetailResponse => {
  if (!appointments) return null;

  return (
    appointments.find(
      (appointment) => appointment.appointmentExternalId === externalId,
    ) || null
  );
};

export const getAppointmentAttendees = (
  appointment: AppointmentDetailResponse,
): AppointmentAttendee[] => {
  if (!appointment) return null;

  return compact<AppointmentAttendee>(
    []
      .concat(appointment.isPatientAttending ? [appointment.patient] : null)
      .concat(appointment.otherAttendees)
      .concat(appointment.supportAttendees)
      .concat(appointment.providerAttendees),
  );
};

/**
 * Get patient appointments tab "current date" from the URL location search value if provided.
 * Otherwise use default date.
 */
export const getDefaultPatientAppointmentsTabDate = (
  searchParams: string,
): Date => {
  const dateValue = getValueFromQueryParams(searchParams, 'date');
  const dateString = dateValue?.includes('T') ? dateValue : `${dateValue}T00:00:00`;
  const parsedDate = new Date(dateString);

  if (!Number.isNaN(parsedDate.valueOf())) {
    return new Date(parsedDate);
  }

  return new Date();
};
